import { useEffect } from 'react'

import {
  AspectRatio,
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from '@chakra-ui/react'
import AgoraRTC, {
  IAgoraRTCRemoteUser,
  LocalVideoTrack,
  RemoteUser,
  useRemoteUsers,
} from 'agora-rtc-react'
import { CameraOff, MicOff, CameraOn } from 'src/components/icon'
import { useModal } from 'src/hooks/use-modal'

import { useVideoCallUsers } from '../../api/getVideoCallUsers'
import { useLocalPlayer } from '../../context/localPlayerContext'
import { useVideoCallRoom } from '../../context/videoCallRoomContext'
import { isScreenShareUserUid } from '../../utils/screenShare'
import { SharedScreenPlayer } from '../SharedScreenPlayer/SharedScreenPlayer'
import { SpeakerIndicator } from '../SpeakerIndicator/SpeakerIndicator'

export const SPVideoCallRoomInCall: React.FC = () => {
  const remoteUsers = useRemoteUsers()
  const { showModal } = useModal()
  const { videoCallUserUid } = useVideoCallRoom()

  const screenSharingRemoteUser = remoteUsers.find(user =>
    isScreenShareUserUid(user.uid),
  )

  const { data: videoCallUsers, mutate } = useVideoCallUsers({
    videoCallUserUids: remoteUsers.map(user => user.uid as number),
  })

  // remoteUsersが変化したときにvideoCallUsersを再取得する
  useEffect(() => {
    mutate()
  }, [remoteUsers, mutate])

  useEffect(() => {
    // iOS系の場合、Agoraの音声の自動再生時にユーザビリティ設定のために失敗することがある。
    // 何かしらユーザーの画面操作を挟めば音声が再生されるため、モーダルを表示・ボタンクリックを促すようにする。
    // See: https://docportal.shengwang.cn/cn/voice-legacy/autoplay_policy_web_ng?platform=iOS
    AgoraRTC.onAutoplayFailed = () => {
      showModal({
        title: '通話が開始されています',
        content: 'マイクをONにしてください',
        submitText: '確認',
        submitButtonColor: 'blue',
        size: 'sm',
        hideCancel: true,
        onSubmit: () => {},
      })
    }
  }, [showModal])

  if (remoteUsers.length === 0) {
    return (
      <Flex w="full" h="full" gap={2}>
        <Center h="full" w="full" color="white">
          {/* TODO: 文言は仮なのでfixする */}
          <Text fontWeight="bold" color="white">
            あなた以外に参加者がいません
          </Text>
        </Center>
        <AspectRatio
          minW="124px"
          ratio={16 / 9}
          w={{ base: '192px', lg: '320px' }}
        >
          <SPVideoCallRoomLocalPlayer />
        </AspectRatio>
      </Flex>
    )
  }

  return !!screenSharingRemoteUser ? (
    <Box boxSize="full">
      <SharedScreenPlayer
        screenSharingRemoteUser={screenSharingRemoteUser}
        colorScheme="green"
        currentUserUid={videoCallUserUid ?? undefined}
      />
    </Box>
  ) : (
    <Flex w="full" h="full" gap={2}>
      <Grid
        w="full"
        h="full"
        gap={4}
        templateColumns={
          remoteUsers.length < 2 ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)'
        }
        templateRows={
          remoteUsers.length < 3 ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)'
        }
      >
        {remoteUsers.slice(0, 3).map(user => (
          <GridItem key={user.uid}>
            <SPVideoCallRoomRemotePlayer
              remoteUser={user}
              remoteUserName={
                videoCallUsers?.find(
                  u => u.videoCallUserUid === (user.uid as number),
                )?.name
              }
            />
          </GridItem>
        ))}
        {remoteUsers.length > 3 && (
          <RestUsersPlaceHolder restNumber={remoteUsers.length - 3} />
        )}
      </Grid>
      <AspectRatio
        minW="124px"
        ratio={16 / 9}
        w={{ base: '192px', lg: '320px' }}
      >
        <SPVideoCallRoomLocalPlayer />
      </AspectRatio>
    </Flex>
  )
}

const RestUsersPlaceHolder = ({ restNumber }: { restNumber: number }) => {
  return (
    <Box
      bg="gray.900"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        backgroundColor="gray.600"
        rounded="full"
        textColor="white"
        width="50px"
        height="50px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        他+{restNumber}
      </Box>
    </Box>
  )
}

const SPVideoCallRoomRemotePlayer: React.FC<{
  remoteUser: IAgoraRTCRemoteUser
  remoteUserName: string | undefined
}> = ({ remoteUser, remoteUserName }) => {
  return (
    <Box w="full" h="full" borderRadius="sm" pos="relative">
      <SpeakerIndicator
        audioTrack={remoteUser.audioTrack}
        enabled={!!remoteUser.audioTrack}
      />

      {remoteUser.videoTrack ? (
        <Flex direction="column" justify="flex-end" w="full" h="full">
          <RemoteUser user={remoteUser} />
        </Flex>
      ) : (
        <Center flexDirection="column" color="white" w="full" h="full">
          <CameraOff size="md" />
        </Center>
      )}
      <Box
        pos="absolute"
        maxW="80%"
        p="0.5"
        left="8px"
        bottom="8px"
        bg="gray.500"
        color="white"
        borderRadius="sm"
        as="span"
      >
        <HStack spacing="2">
          <Text
            fontSize="x-small"
            as="span"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {remoteUserName || remoteUser.uid}
          </Text>
          {!remoteUser.audioTrack && (
            <Box minW="16px">
              <MicOff size="sm" />
            </Box>
          )}
        </HStack>
      </Box>
    </Box>
  )
}

const SPVideoCallRoomLocalPlayer = () => {
  const {
    localCameraEnabled,
    localCameraTrack,
    localMicrophoneTrack: audioTrack,
    localMicrophoneEnabled,
  } = useLocalPlayer()

  return (
    <Box h="full" w="full" bg="gray.700" pos="relative" borderRadius="sm">
      <SpeakerIndicator
        audioTrack={audioTrack}
        enabled={localMicrophoneEnabled}
      />
      {localCameraEnabled ? (
        <Flex
          direction="column"
          justify="flex-end"
          w="full"
          h="full"
          transform="scaleX(-1)"
        >
          <LocalVideoTrack
            track={localCameraTrack}
            play
            style={{
              width: '100%',
              height: '100%',
              // ミラーになるように
              transform: 'scaleX(-1)',
            }}
          />
        </Flex>
      ) : (
        <Center flexDirection="column" color="white" w="full" h="full">
          {localCameraEnabled ? <CameraOn /> : <CameraOff />}
        </Center>
      )}
      <Box
        pos="absolute"
        p="0.5"
        left="8px"
        bottom="8px"
        bg="gray.500"
        color="white"
        borderRadius="sm"
        as="span"
      >
        <HStack spacing="2">
          <Text fontSize="x-small" as="span">
            あなた
          </Text>
          {!localMicrophoneEnabled && <MicOff size="sm" />}
        </HStack>
      </Box>
    </Box>
  )
}
